import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Circle, Copy, ExternalLink, Loader2, RotateCcw, Users } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";

const SITEMAP_URL = "https://www.aureum7trading.com/sitemap.xml";
const SITEMAP_IMAGES_URL = "https://www.aureum7trading.com/sitemap-images.xml";

type Step = {
  id: string;
  engine: "Google" | "Bing" | "IndexNow" | "Yandex";
  title: string;
  description: string;
  actionUrl: string;
  actionLabel: string;
};

const STEPS: Step[] = [
  {
    id: "gsc-en",
    engine: "Google",
    title: "Google Search Console — Primary property",
    description:
      "Open Search Console, select the www.aureum7trading.com property, then paste sitemap.xml in Sitemaps → Add a new sitemap.",
    actionUrl:
      "https://search.google.com/search-console/sitemaps?resource_id=https%3A%2F%2Fwww.aureum7trading.com%2F",
    actionLabel: "Open GSC Sitemaps",
  },
  {
    id: "gsc-images",
    engine: "Google",
    title: "Google Search Console — Image sitemap",
    description: "Submit sitemap-images.xml under the same property to expose product imagery.",
    actionUrl:
      "https://search.google.com/search-console/sitemaps?resource_id=https%3A%2F%2Fwww.aureum7trading.com%2F",
    actionLabel: "Open GSC Sitemaps",
  },
  {
    id: "bing",
    engine: "Bing",
    title: "Bing Webmaster Tools — Submit sitemap",
    description:
      "Open Bing Webmaster, select your site, then Sitemaps → Submit sitemap and paste the URL.",
    actionUrl: "https://www.bing.com/webmasters/sitemaps",
    actionLabel: "Open Bing Webmaster",
  },
  {
    id: "bing-urls",
    engine: "Bing",
    title: "Bing Webmaster Tools — URL Inspection (priority pages)",
    description:
      "Use URL Inspection to request indexing of the homepage, /smart-toilets, /bath-sanitary and the 5 market pages.",
    actionUrl: "https://www.bing.com/webmasters/urlinspection",
    actionLabel: "Open URL Inspection",
  },
  {
    id: "yandex",
    engine: "Yandex",
    title: "Yandex Webmaster — Sitemap (optional)",
    description: "If you target Russian-speaking visitors, also submit the sitemap to Yandex.",
    actionUrl: "https://webmaster.yandex.com/sites/",
    actionLabel: "Open Yandex Webmaster",
  },
  {
    id: "indexnow",
    engine: "IndexNow",
    title: "IndexNow ping (Bing + Yandex instant)",
    description:
      "Trigger the project's One-Click SEO automation flow to ping IndexNow with all priority URLs.",
    actionUrl: "/en/seo-dashboard",
    actionLabel: "Open SEO Dashboard",
  },
];

type LogRow = {
  step_id: string;
  engine: string;
  submitted_at: string;
  submitted_by_email: string | null;
};

const SitemapSubmission = () => {
  const [log, setLog] = useState<Record<string, LogRow>>({});
  const [user, setUser] = useState<{ id: string; email: string | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Auth
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      const u = data.session?.user;
      setUser(u ? { id: u.id, email: u.email ?? null } : null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      const u = session?.user;
      setUser(u ? { id: u.id, email: u.email ?? null } : null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // Initial load + realtime sync
  useEffect(() => {
    let active = true;
    const load = async () => {
      const { data, error } = await supabase
        .from("sitemap_submission_log")
        .select("step_id, engine, submitted_at, submitted_by_email");
      if (!active) return;
      if (error) {
        toast({ title: "Could not load shared log", description: error.message, variant: "destructive" });
      } else if (data) {
        const map: Record<string, LogRow> = {};
        data.forEach((r) => (map[r.step_id] = r as LogRow));
        setLog(map);
      }
      setLoading(false);
    };
    load();

    const channel = supabase
      .channel("sitemap-submission-log")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "sitemap_submission_log" },
        (payload) => {
          if (payload.eventType === "DELETE") {
            const old = payload.old as LogRow;
            setLog((prev) => {
              const next = { ...prev };
              delete next[old.step_id];
              return next;
            });
          } else {
            const row = payload.new as LogRow;
            setLog((prev) => ({ ...prev, [row.step_id]: row }));
          }
        }
      )
      .subscribe();

    return () => {
      active = false;
      supabase.removeChannel(channel);
    };
  }, []);

  const markSubmitted = async (step: Step) => {
    if (!user) {
      toast({ title: "Sign in required", description: "Sign in to record submissions for the team.", variant: "destructive" });
      return;
    }
    setBusyId(step.id);
    const sitemapUrl = step.id === "gsc-images" ? SITEMAP_IMAGES_URL : SITEMAP_URL;
    const { error } = await supabase
      .from("sitemap_submission_log")
      .upsert(
        {
          step_id: step.id,
          engine: step.engine,
          sitemap_url: sitemapUrl,
          submitted_at: new Date().toISOString(),
          submitted_by: user.id,
          submitted_by_email: user.email,
        },
        { onConflict: "step_id" }
      );
    setBusyId(null);
    if (error) {
      toast({ title: "Failed to record", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Submission recorded", description: "Synced to your team." });
    }
  };

  const undo = async (stepId: string) => {
    setBusyId(stepId);
    const { error } = await supabase.from("sitemap_submission_log").delete().eq("step_id", stepId);
    setBusyId(null);
    if (error) {
      toast({ title: "Could not undo", description: error.message, variant: "destructive" });
    }
  };

  const resetAll = async () => {
    if (!confirm("Clear all submission timestamps for the whole team?")) return;
    const { error } = await supabase.from("sitemap_submission_log").delete().gt("submitted_at", "1970-01-01");
    if (error) toast({ title: "Reset failed", description: error.message, variant: "destructive" });
  };

  const exportLog = () => {
    const blob = new Blob([JSON.stringify(log, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sitemap-submission-log-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied", description: text });
  };

  const completed = STEPS.filter((s) => log[s.id]).length;

  return (
    <div className="min-h-screen bg-neutral-50">
      <Helmet>
        <title>Sitemap Submission Checklist | Aureum VII Trading SEO Ops</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="mx-auto max-w-4xl px-4 py-12">
        <div className="mb-8">
          <h1 className="font-serif text-3xl font-bold text-neutral-900 sm:text-4xl">
            Sitemap Submission Checklist
          </h1>
          <p className="mt-2 text-neutral-600">
            Step-by-step submission guide for Google, Bing, Yandex and IndexNow.{" "}
            <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-2 py-0.5 text-xs text-emerald-800">
              <Users className="h-3 w-3" /> Shared across team & devices
            </span>
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-neutral-700">
            <Badge variant="secondary">
              {completed} / {STEPS.length} completed
            </Badge>
            {user ? (
              <span className="text-xs text-neutral-500">Signed in as {user.email}</span>
            ) : (
              <Link to="/en/auth" className="text-xs text-amber-700 underline">
                Sign in to record submissions
              </Link>
            )}
            <Button variant="ghost" size="sm" onClick={resetAll} disabled={!user}>
              <RotateCcw className="mr-1 h-3 w-3" /> Reset all
            </Button>
            <Button variant="ghost" size="sm" onClick={exportLog}>
              Export log JSON
            </Button>
          </div>
        </div>

        <Card className="mb-6 border-amber-300 bg-amber-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-neutral-900">Sitemap URLs (copy first)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {[SITEMAP_URL, SITEMAP_IMAGES_URL].map((u) => (
              <div
                key={u}
                className="flex items-center justify-between gap-2 rounded border border-amber-200 bg-white px-3 py-2 font-mono text-xs"
              >
                <span className="truncate">{u}</span>
                <Button size="sm" variant="outline" onClick={() => copy(u)}>
                  <Copy className="mr-1 h-3 w-3" /> Copy
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center gap-2 text-neutral-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading shared submission log…
          </div>
        ) : (
          <ol className="space-y-4">
            {STEPS.map((step, idx) => {
              const entry = log[step.id];
              const done = !!entry;
              return (
                <Card key={step.id} className={done ? "border-emerald-300" : ""}>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-start gap-3 text-base">
                      {done ? (
                        <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
                      ) : (
                        <Circle className="mt-0.5 h-5 w-5 flex-shrink-0 text-neutral-400" />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-neutral-500">Step {idx + 1}</span>
                          <Badge variant="outline">{step.engine}</Badge>
                        </div>
                        <div className="mt-1 font-serif text-lg text-neutral-900">{step.title}</div>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="mb-3 text-sm text-neutral-600">{step.description}</p>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button asChild variant="outline" size="sm">
                        <a href={step.actionUrl} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="mr-1 h-3 w-3" /> {step.actionLabel}
                        </a>
                      </Button>
                      {!done ? (
                        <Button
                          size="sm"
                          onClick={() => markSubmitted(step)}
                          disabled={busyId === step.id || !user}
                        >
                          {busyId === step.id && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                          Mark as submitted
                        </Button>
                      ) : (
                        <>
                          <span className="text-xs text-emerald-700">
                            Submitted {new Date(entry.submitted_at).toLocaleString()}
                            {entry.submitted_by_email ? ` by ${entry.submitted_by_email}` : ""}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => undo(step.id)}
                            disabled={busyId === step.id}
                          >
                            Undo
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => markSubmitted(step)}
                            disabled={busyId === step.id || !user}
                          >
                            Re-submit (update timestamp)
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </ol>
        )}

        <div className="mt-8 rounded-lg border border-neutral-200 bg-white p-4 text-sm text-neutral-600">
          <strong className="text-neutral-900">Tip:</strong> Submissions are stored centrally and
          stream live across team browsers via realtime sync. The exported JSON log can be attached
          to your monthly SEO report.
        </div>
      </div>
    </div>
  );
};

export default SitemapSubmission;
